import React from "react";
import { Info, ExternalLink } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { SENTIMENT_LABELS, sentimentBadgeClasses } from "../lib/format";

/**
 * Small info button that, when clicked, reveals:
 *   - How the metric is calculated (one sentence)
 *   - The workshops that contribute to this number
 *   - The reasoning field relevant to this metric for each workshop
 *
 * Props:
 *   - calc: string  — short definition
 *   - evidence: Array<{ workshop, text }>
 *   - onSelect?: (workshopId: string) => void — opens the existing drawer
 *   - testid?: string
 */
export default function KpiDisclosure({ calc, evidence, onSelect, testid }) {
  const count = evidence?.length || 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-400 hover:text-[#2F4F4F] focus:outline-none focus:text-[#2F4F4F]"
          data-testid={testid || "kpi-disclosure-trigger"}
          aria-label="Show methodology and evidence"
        >
          <Info className="h-3 w-3" />
          Evidence
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[380px] max-h-[420px] overflow-y-auto p-0 rounded-sm border border-gray-200 shadow-lg"
        data-testid="kpi-disclosure-panel"
      >
        <div className="p-4 border-b border-gray-100 bg-[#F8FAF9]">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#2F4F4F]">
            How this is calculated
          </div>
          <p className="text-xs text-gray-700 mt-1 leading-relaxed">{calc}</p>
          <div className="text-[11px] text-gray-500 font-mono mt-2">
            {count} contributing workshop{count === 1 ? "" : "s"}
          </div>
        </div>
        <div className="p-2">
          {count === 0 ? (
            <p className="p-3 text-xs text-gray-500">
              No workshops contribute to this metric.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {evidence.map((e, i) => (
                <li
                  key={`${e.workshop.id}-${i}`}
                  className="p-3"
                  data-testid={`kpi-disclosure-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {e.workshop.workshop_name}
                    </div>
                    {e.workshop.sentiment ? (
                      <span
                        className={
                          "inline-block px-1.5 py-0.5 text-[10px] font-medium border " +
                          sentimentBadgeClasses(e.workshop.sentiment)
                        }
                      >
                        {SENTIMENT_LABELS[e.workshop.sentiment] || ""}
                      </span>
                    ) : null}
                  </div>
                  {e.text ? (
                    <p className="text-xs text-gray-700 mt-1 leading-snug">
                      {e.text}
                    </p>
                  ) : null}
                  <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                    {onSelect ? (
                      <button
                        type="button"
                        onClick={() => onSelect(e.workshop.id)}
                        className="text-[#2F4F4F] hover:underline"
                        data-testid={`kpi-disclosure-open-${i}`}
                      >
                        Open workshop →
                      </button>
                    ) : null}
                    {e.workshop.source_url ? (
                      <a
                        href={e.workshop.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
                      >
                        PDF <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
